/**
 * Escapes draft content that would break MDXEditor's parser (it shows an empty editor on error).
 * Level 0 always runs; higher levels only after a parse error.
 */

/** Highest sanitization level `sanitizeDraftMarkdown` supports. */
export const MAX_DRAFT_SANITIZE_LEVEL = 2;

// Splits out markdown link destinations (`](…)`) so escaping never corrupts
// citation URLs; the escape callback receives only the surrounding text.
const escapeOutsideLinkDestinations = (markdown: string, escape: (segment: string) => string): string =>
  markdown
    .split(/(\]\([^)\s]*\))/g)
    .map((part, index) => (index % 2 === 1 ? part : escape(part)))
    .join('');

/**
 * Cumulative levels:
 * - 0 (always): escape `*` between alphanumerics (`10*6/L`).
 * - 1: escape every unescaped `*`, `_`, `~` outside link destinations.
 * - 2: flatten `<ul>`/`<ol>`/`<li>` to `•` and escape remaining `<` and `{`.
 */
export const sanitizeDraftMarkdown = (markdown: string, level = 0): string =>
  escapeOutsideLinkDestinations(markdown, (segment) => {
    let text = segment.replace(/(?<=[A-Za-z0-9])\*(?=[A-Za-z0-9])/g, '\\*');
    if (level >= 1) {
      text = text.replace(/\\[*_~]|[*_~]/g, (match) => (match.length === 2 ? match : `\\${match}`));
    }
    if (level >= 2) {
      text = text
        .replace(/<\/?(?:ul|ol)>/gi, '')
        .replace(/<li>/gi, ' • ')
        .replace(/<\/li>/gi, '')
        .replace(/\\[<{]|[<{]/g, (match) => (match.length === 2 ? match : `\\${match}`));
    }
    return text;
  });
